import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { IProviders, RootState } from 'store/store';
import {
  pureSearchDocuments,
  requireDocument,
  requireDocumentFn,
  requireDocumentInFolder,
  requireDocumentInFolderFn,
} from 'store/providers/providerSlice';
import Modal from 'components/Modal';
import ItemPicker from 'components/ItemPicker';
import { AxiosPromise } from 'axios';
import { appDispatch } from 'store/store';
import {
  IProviderItemView,
  IRelatedProviderItem,
} from 'store/providers/providerTypes';
import { ICompanyProviders } from 'store/companies/companiesTypes';
import { IItemPicker } from 'store/entity/types';
import i18n from '../../../../i18n';

interface IDispatchToProps {
  requireSingleDocument: (
    documentId: number
  ) => ReturnType<typeof requireDocumentFn>;
  requireDocumentInFolder: (
    folderId: number,
    providerId: number
  ) => ReturnType<typeof requireDocumentInFolderFn>;
  pureSearchDocuments: (
    providerId: number,
    query: string,
    folderId?: number
  ) => AxiosPromise<IProviderItemView[]>;
}

interface IStateToProps {
  providerItems: IProviders['items'];
}

interface IComponentStates {
  loading: boolean;
  providerId: number;
}

interface IOwnProps {
  onClose: () => void;
  onSave: (item: IRelatedProviderItem) => void;
  providers: ICompanyProviders;
  defaultItem?: IRelatedProviderItem;
}

type IComponentProps = IOwnProps & IDispatchToProps & IStateToProps;

class TaskFileExplorerModal extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  modalTabs = Object.values(this.props?.providers || {})?.reduce(
    (temp: any, provider: any) => {
      temp.push({ id: provider.id, title: provider.name });
      return temp;
    },
    []
  );

  state: IComponentStates = {
    loading: false,
    providerId: this.props.defaultItem
      ? this.props.defaultItem.providerId
      : this.modalTabs[0].id,
  };

  componentDidMount() {
    const { providerItems, providers, defaultItem } = this.props;
    this.handleRequireDocumentsInFolder(
      providers[this.state.providerId].rootDocumentId
    );
    if (defaultItem) {
      const dadItemId =
        providerItems[defaultItem.providerId].items[defaultItem.id]?.data
          ?.dadId;
      if (dadItemId) this.handleRequireFolderAndItems(dadItemId);
    }
  }

  componentDidUpdate(_prevProps: IComponentProps, prevState: IComponentStates) {
    const { providerItems, providers, defaultItem } = this.props;
    const { providerId } = this.state;
    if (prevState.providerId !== providerId) {
      if (this.isRootFolderLoaded()) {
        this.setState({ loading: false });
        return;
      }

      this.handleRequireDocumentsInFolder(providers[providerId].rootDocumentId);
      if (defaultItem) {
        const dadItemId =
          providerItems[defaultItem.providerId].items[defaultItem.id].data
            .dadId;
        this.handleRequireFolderAndItems(dadItemId);
      }
      this.setState({ loading: false });
    }
  }

  isRootFolderLoaded = () => {
    const { providerItems, providers } = this.props;
    const rootDocumentId = providers[this.state.providerId]?.rootDocumentId;
    return (
      rootDocumentId &&
      providerItems[this.state.providerId]?.folders[rootDocumentId]?.state ===
        'loaded'
    );
  };

  handleRequireDocumentsInFolder = (requiredFolderId: number) => {
    return this.props.requireDocumentInFolder(
      requiredFolderId,
      this.state.providerId
    );
  };

  handleRequireFolderAndItems = (requiredFolderId: number) => {
    const dadItemId =
      this.props.providerItems[this.state.providerId].items[requiredFolderId];
    if (dadItemId) {
      return Promise.resolve();
    } else {
      return Promise.all([
        this.props.requireSingleDocument(requiredFolderId),
        this.handleRequireDocumentsInFolder(requiredFolderId),
      ]);
    }
  };

  handleSelectATab = (providerId: number | string) => {
    this.setState({ providerId: providerId as number, loading: true });
  };

  render() {
    const { defaultItem, onClose, onSave, providerItems, providers } =
      this.props;

    const { loading, providerId } = this.state;

    const isRootFolderLoaded = this.isRootFolderLoaded();

    let selectedItem: IItemPicker | null = null;
    if (
      defaultItem &&
      isRootFolderLoaded &&
      defaultItem.providerId === providerId
    ) {
      const relatedProviderItem =
        providerItems[providerId].items[defaultItem.id]?.data;
      if (relatedProviderItem)
        selectedItem = {
          id: relatedProviderItem.id,
          name: relatedProviderItem.name!,
          dadId: relatedProviderItem.dadId,
          type: relatedProviderItem.type,
        };
    }
    return (
      <Modal
        width="50%"
        tabs={this.modalTabs}
        title={i18n.t('addContent', { ns: 'taskManager' })}
        onClose={onClose}
        isActive={true}
        selectedTab={providerId}
        onSelectATab={this.handleSelectATab}
      >
        {!loading && isRootFolderLoaded ? (
          <ItemPicker
            providerId={Number(providerId)}
            onSaveItem={onSave}
            providerItems={providerItems[providerId].items!}
            rootFolder={{
              id: providers[providerId].rootDocumentId,
              name: i18n.t('rootFolderName', {
                ns: 'taskManager',
                folderName: providers[providerId].name,
              }),
              dadId: -1,
              type: 'folder',
            }}
            selectedProviderItem={selectedItem}
            onRequireFolderAndItems={this.handleRequireFolderAndItems}
            onRequireDocumentsInFolder={this.handleRequireDocumentsInFolder}
            onSearchForDocuments={(text, dadFolderId) =>
              this.props.pureSearchDocuments(
                Number(providerId),
                text,
                dadFolderId
              )
            }
          />
        ) : (
          <p style={{ alignSelf: 'center', textAlign: 'center' }}>
            {i18n.t('loading', { ns: 'taskManager' }) as string}
          </p>
        )}
      </Modal>
    );
  }
}

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    requireSingleDocument: (documentId: number) =>
      appDispatch(requireDocument({ documentId })),
    requireDocumentInFolder: (folderId: number, providerId: number) =>
      appDispatch(requireDocumentInFolder({ folderId, providerId })),
    pureSearchDocuments: (
      providerId: number,
      query: string,
      folderId?: number
    ) =>
      appDispatch(
        pureSearchDocuments({
          query: query,
          providerId: providerId,
          start: 0,
          count: 100,
          folderId: folderId,
        })
      ),
  };
};

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const providerItems = state.providers.items;
  return { providerItems };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TaskFileExplorerModal);
